import type {
  LogEntry,
  InteractivePromptToolName,
  AskUserArguments,
  SimpleAskUserArguments,
  CommandType,
  ExitPlanModeArguments,
  PermissionRequestArguments,
  CompletionChunk,
  HydratedLogEntry,
  ToolExecutionStartMessage,
  ToolUserRequestedMessage,
  RequestedSchema,
  RequestedSchemaProperty,
} from '../../types/session'
import {
  INTERACTIVE_PROMPT_TOOL_NAMES,
  isToolUserRequestedMessage,
  isToolExecutionStartMessage,
  isCompletionChunk,
  isCompletionToolMessage,
  isToolOutputsMessage,
} from '../../types/session'
import {isExitPlanModeRequestedMessage} from '../../types/session-events'
import {COMMAND_TYPES} from '../../constants/command-types'

/**
 * Normalize raw ask_user arguments into the canonical AskUserArguments union.
 *
 * Both ask_user variants (legacy choices and elicitation schema) arrive over the
 * wire with `toolName: "ask_user"` because the MCP SDK registers them under the
 * same name. The only way to distinguish them is by inspecting the argument
 * shape — specifically the presence of a `requestedSchema` field.
 */
export function normalizeAskUserArguments(raw: Record<string, unknown>): AskUserArguments {
  const schema = parseRequestedSchema(raw.requestedSchema)
  if (schema) {
    return {
      message: typeof raw.message === 'string' ? raw.message : '',
      requestedSchema: schema,
    }
  }

  const question = typeof raw.question === 'string' ? raw.question : ''

  // Legacy sends `allowFreeform` (camelCase) or `allow_freeform` (snake_case)
  let allowFreeform: boolean | undefined
  if (typeof raw.allowFreeform === 'boolean') {
    allowFreeform = raw.allowFreeform
  } else if (typeof raw.allow_freeform === 'boolean') {
    allowFreeform = raw.allow_freeform
  }

  const result: SimpleAskUserArguments = {question}
  if (Array.isArray(raw.choices)) {
    result.choices = raw.choices as string[]
  }
  if (allowFreeform !== undefined) {
    result.allowFreeform = allowFreeform
  }
  return result
}

type ExtractedChoices = {
  values: string[]
  labels?: Record<string, string>
}

/**
 * Recursively extract enumerable choices from any JSON Schema construct.
 * Handles: `enum`, `oneOf[{const,title}]`, `anyOf[{const,title}]`,
 * and `items.{enum,oneOf,anyOf}` for array types.
 * Returns undefined if no choices are found.
 */
function extractChoices(node: Record<string, unknown>): ExtractedChoices | undefined {
  // Direct enum
  if (Array.isArray(node.enum)) {
    const values = node.enum.filter((v): v is string => typeof v === 'string')
    if (values.length > 0) return {values}
  }

  // oneOf/anyOf with const+title entries
  const constTitleSource = Array.isArray(node.oneOf) ? node.oneOf : Array.isArray(node.anyOf) ? node.anyOf : null
  if (constTitleSource) {
    const result = extractConstTitleEntries(constTitleSource)
    if (result) return result
  }

  // Recurse into items (for type: "array")
  if (node.items && typeof node.items === 'object') {
    return extractChoices(node.items as Record<string, unknown>)
  }

  return undefined
}

/** Extract values and labels from a oneOf/anyOf array of {const, title} entries. */
function extractConstTitleEntries(entries: unknown[]): ExtractedChoices | undefined {
  const values: string[] = []
  const labels: Record<string, string> = {}
  for (const entry of entries) {
    if (entry && typeof entry === 'object') {
      const item = entry as Record<string, unknown>
      if (typeof item.const === 'string') {
        values.push(item.const)
        if (typeof item.title === 'string') {
          labels[item.const] = item.title
        }
      }
    }
  }

  if (values.length === 0) return undefined
  return Object.keys(labels).length > 0 ? {values, labels} : {values}
}

/**
 * Validate and parse a raw `requestedSchema` value into a typed `RequestedSchema`.
 * Returns `undefined` if the value is missing, not an object, or structurally invalid.
 */
function parseRequestedSchema(value: unknown): RequestedSchema | undefined {
  if (!value || typeof value !== 'object') return undefined

  const obj = value as Record<string, unknown>
  if (!obj.properties || typeof obj.properties !== 'object') return undefined

  const properties: Record<string, RequestedSchemaProperty> = {}
  for (const [key, propValue] of Object.entries(obj.properties as Record<string, unknown>)) {
    if (!propValue || typeof propValue !== 'object') continue
    const prop = propValue as Record<string, unknown>
    const parsed: RequestedSchemaProperty = {}
    if (typeof prop.type === 'string') parsed.type = prop.type as RequestedSchemaProperty['type']
    if (typeof prop.title === 'string') parsed.title = prop.title
    if (typeof prop.description === 'string') parsed.description = prop.description
    if (typeof prop.default === 'string') parsed.default = prop.default

    if (parsed.type === 'array') {
      parsed.multiSelect = true
      // Inherit title from items if the property itself has no title
      if (!parsed.title && prop.items && typeof prop.items === 'object') {
        const items = prop.items as Record<string, unknown>
        if (typeof items.title === 'string') parsed.title = items.title
      }
    }

    const choices = extractChoices(prop)
    if (choices) {
      parsed.enum = choices.values
      if (choices.labels) parsed.enumLabels = choices.labels
    }

    properties[key] = parsed
  }

  if (Object.keys(properties).length === 0) return undefined

  const result: RequestedSchema = {properties}
  if (Array.isArray(obj.required)) {
    result.required = obj.required.filter((v): v is string => typeof v === 'string')
  }

  return result
}

/**
 * Normalizes arguments for ask_user prompts across all entry formats.
 */
function normalizePromptArguments(
  toolName: InteractivePromptToolName,
  args: AskUserArguments | ExitPlanModeArguments | PermissionRequestArguments | unknown,
): AskUserArguments | ExitPlanModeArguments | PermissionRequestArguments {
  if (toolName === 'ask_user') {
    return normalizeAskUserArguments(args as Record<string, unknown>)
  }

  return args as ExitPlanModeArguments | PermissionRequestArguments
}

/** Type for ToolExecutionStartMessage specifically for interactive prompt tools */
export type InteractivePromptToolExecution = ToolExecutionStartMessage & {
  data: {
    toolCallId: string
    toolName: InteractivePromptToolName
    arguments: AskUserArguments | ExitPlanModeArguments | PermissionRequestArguments
  }
}

/**
 * Check if a log entry is a tool.execution_start event for an interactive prompt tool.
 */
export function isInteractivePromptToolExecution(logEntry: LogEntry): logEntry is InteractivePromptToolExecution {
  if (!isToolExecutionStartMessage(logEntry)) {
    return false
  }
  return INTERACTIVE_PROMPT_TOOL_NAMES.has(logEntry.data.toolName as InteractivePromptToolName)
}

/** Extracted prompt data from a CompletionChunk */
export type ExtractedInteractivePromptData = {
  toolCallId: string
  toolName: InteractivePromptToolName
  arguments: AskUserArguments | ExitPlanModeArguments | PermissionRequestArguments
}

/**
 * Extract all interactive prompt tool calls from a CompletionChunk.
 */
export function extractInteractivePromptsFromChunk(chunk: CompletionChunk): ExtractedInteractivePromptData[] {
  const prompts: ExtractedInteractivePromptData[] = []

  for (const choice of chunk.choices) {
    if (!choice.delta.tool_calls) continue

    for (const toolCall of choice.delta.tool_calls) {
      if (
        !toolCall.function?.name ||
        !INTERACTIVE_PROMPT_TOOL_NAMES.has(toolCall.function.name as InteractivePromptToolName)
      ) {
        continue
      }

      // Skip exit_plan_mode in CompletionChunks — we use the richer exit_plan_mode.requested event instead
      if (toolCall.function.name === 'exit_plan_mode') {
        continue
      }

      let parsedArgs: AskUserArguments | ExitPlanModeArguments | PermissionRequestArguments
      try {
        parsedArgs = toolCall.function.arguments ? JSON.parse(toolCall.function.arguments) : {}
      } catch {
        parsedArgs = {} as AskUserArguments
      }

      const toolName = toolCall.function.name as InteractivePromptToolName
      prompts.push({
        toolCallId: toolCall.id,
        toolName,
        arguments: normalizePromptArguments(toolName, parsedArgs),
      })
    }
  }

  return prompts
}

/**
 * Check if a CompletionChunk contains an interactive prompt tool call.
 */
export function hasInteractivePromptInChunk(logEntry: LogEntry): logEntry is CompletionChunk {
  if (!isCompletionChunk(logEntry as HydratedLogEntry)) {
    return false
  }
  return extractInteractivePromptsFromChunk(logEntry as CompletionChunk).length > 0
}

/**
 * Extract interactive prompt data from a CompletionChunk.
 * Returns the first interactive prompt found, or null if none.
 */
export function extractInteractivePromptFromChunk(chunk: CompletionChunk): ExtractedInteractivePromptData | null {
  return extractInteractivePromptsFromChunk(chunk)[0] ?? null
}

/**
 * Convert extracted prompt data to ToolUserRequestedMessage format.
 */
export function toToolUserRequestedMessage(
  prompt: ExtractedInteractivePromptData,
  timestamp?: string,
): ToolUserRequestedMessage {
  return {
    type: 'tool.user_requested',
    toolCallId: prompt.toolCallId,
    toolName: prompt.toolName,
    arguments: prompt.arguments,
    timestamp: timestamp || new Date().toISOString(),
  }
}

/**
 * Normalized prompt data structure used across the UI.
 * Abstracts over the different event formats (tool.user_requested, tool.execution_start, CompletionChunk).
 */
export type NormalizedPrompt = {
  toolCallId: string
  toolName: InteractivePromptToolName
  arguments: AskUserArguments | ExitPlanModeArguments | PermissionRequestArguments
}

/**
 * Find the tool response for a given tool call ID in the logs.
 * Returns the response content if found, undefined otherwise.
 * Checks both CompletionToolMessage (role: 'tool') and ToolOutputsMessage (type: 'tool.outputs') formats.
 */
export function findToolResponse(toolCallId: string, logsData?: LogEntry[]): string | undefined {
  if (!logsData) return undefined

  for (const entry of logsData) {
    if (isCompletionToolMessage(entry) && entry.tool_call_id === toolCallId) {
      if (typeof entry.content === 'string') {
        return entry.content
      }

      if (Array.isArray(entry.content)) {
        return entry.content
          .filter((part): part is {type: 'text'; text: string} => part.type === 'text')
          .map(part => part.text)
          .join('')
      }
    }

    if (isToolOutputsMessage(entry) && entry.toolCallId === toolCallId) {
      return entry.output
    }
  }
  return undefined
}

/**
 * Extract a display-friendly answer from an optimistic response.
 * Uses the commandType (already set by each prompt component) as the discriminant
 * instead of duck-typing the response object.
 */
export function formatOptimisticResponse(response: unknown, commandType: CommandType): string | undefined {
  if (response == null) return undefined
  if (typeof response !== 'object') return String(response)

  const obj = response as Record<string, unknown>

  switch (commandType) {
    case COMMAND_TYPES.ASK_USER_RESPONSE:
      return obj.answer != null ? String(obj.answer) : undefined

    case COMMAND_TYPES.ELICITATION_RESPONSE:
      if (obj.action === 'decline' || obj.action === 'cancel') return 'Declined'
      if (obj.action === 'accept' && obj.content && typeof obj.content === 'object') {
        return formatElicitationContent(obj.content as Record<string, unknown>)
      }
      return undefined

    case COMMAND_TYPES.PERMISSION_RESPONSE:
      return obj.approved ? 'approved' : 'denied-interactively-by-user'

    case COMMAND_TYPES.PLAN_APPROVAL_RESPONSE:
      if (obj.selectedAction != null) return String(obj.selectedAction)
      if (obj.feedback != null) return `Feedback: ${String(obj.feedback)}`
      return undefined

    default:
      return String(response)
  }
}

/**
 * Format elicitation content for display. Produces a human-readable
 * key: value list matching the runtime's detailed format.
 */
export function formatElicitationContent(content: Record<string, unknown>): string {
  const entries = Object.entries(content)
  if (entries.length === 0) return ''
  return entries
    .map(([key, value]) => {
      const formatted = Array.isArray(value) ? value.join(', ') : String(value)
      return `${key}: ${formatted}`
    })
    .join('\n')
}

/**
 * Extracts a normalized prompt from various log entry formats.
 */
export function extractPromptFromEntry(entry: LogEntry): NormalizedPrompt | null {
  // Handle tool.user_requested format
  if (isToolUserRequestedMessage(entry)) {
    const msg = entry
    if (INTERACTIVE_PROMPT_TOOL_NAMES.has(msg.toolName)) {
      return {
        toolCallId: msg.toolCallId,
        toolName: msg.toolName,
        arguments: normalizePromptArguments(msg.toolName, msg.arguments),
      }
    }
  }

  // Handle tool.execution_start format
  if (isInteractivePromptToolExecution(entry)) {
    const msg = entry
    return {
      toolCallId: msg.data.toolCallId,
      toolName: msg.data.toolName,
      arguments: normalizePromptArguments(msg.data.toolName, msg.data.arguments),
    }
  }

  // Handle exit_plan_mode.requested event — native event with richer data (includes planContent)
  if (isExitPlanModeRequestedMessage(entry)) {
    return {
      toolCallId: entry.data.toolCallId,
      toolName: 'exit_plan_mode',
      arguments: {
        summary: entry.data.summary,
        planContent: entry.data.planContent,
        actions: entry.data.actions,
        recommendedAction: entry.data.recommendedAction,
      } satisfies ExitPlanModeArguments,
    }
  }

  // CompletionChunk format — normalization already handled in extractInteractivePromptFromChunk
  if (hasInteractivePromptInChunk(entry)) {
    const promptData = extractInteractivePromptFromChunk(entry)
    if (promptData && INTERACTIVE_PROMPT_TOOL_NAMES.has(promptData.toolName)) {
      return {
        toolCallId: promptData.toolCallId,
        toolName: promptData.toolName,
        arguments: promptData.arguments,
      }
    }
  }

  return null
}

/**
 * Gets the tool call ID from a log entry if it's an interactive prompt.
 */
export function getPromptToolCallId(entry: LogEntry): string | null {
  const prompt = extractPromptFromEntry(entry)
  return prompt?.toolCallId ?? null
}

export type PendingPromptResult = {
  /** The pending prompt that needs user interaction, or null if none */
  pendingPrompt: NormalizedPrompt | null
  /** The tool call ID of the pending prompt, for filtering */
  pendingToolCallId: string | null
  /** Whether any prompt is currently pending */
  hasPendingPrompt: boolean
}
